// Per-tile shader flags as an RGBA8 data texture (ROADMAP P7.13).
//
// The tile program is at the driver's vertex-attribute limit, so the flags
// used to pack into `aTone.z` — river-edge mask in bits 0..5, volcano code
// above (×64), the landlocked wall at 4×64 = bit 8 — capped at 511
// (float-exact) with no room for the next feature. Now:
//
//   R = river-edge mask (6 bits, 0..63)
//   G = tile code: 0 none, 1 dormant, 2 active, 3 erupting (volcano),
//       4 = landlocked wall — the same code space the old `code × 64` used
//   B = terrain level (0..=12; heights are CPU-side today, so B is the level
//       shader passes that don't exist yet — free room, no packing math)
//   A = reserved for the next flag
//
// `aTone.z` now carries the tile's GLOBAL index instead (entries.idx — exact
// in float32 for any board), the vertex shader does `texelFetch` and composes
// the legacy `_vRiverEdge` int bit-for-bit (`R | G << 6` IS the old
// `riv + code * 64`: 4 << 6 = 256 = bit 8), so no consumer GLSL changed —
// `volcanoTileShader.ts` decodes the same int it always did.
//
// Built once per terrain signature with a single-entry cache, mirroring
// `shoreBoardFor` (renderer-crash-rca #139/#140): the snapshot's tile array
// is fresh on every publish, so caching on array identity would rebuild the
// texture (and its GPU upload) every publish for nothing.

import type { SceneNode } from '@aod/protocol';
import * as THREE from 'three';

export interface TileData {
  readonly signature: string;
  readonly texture: THREE.DataTexture;
  readonly width: number;
  readonly height: number;
}

/**
 * Everything that changes a texel: position (texel order!), river mask,
 * terrain level, and the tile code (volcano + wall — both come from the
 * single code map HexGrid merges them into). Hashed in ARRAY order on
 * purpose: texel i IS tiles[i], and a fresh publish reuses the board order.
 */
export function tileDataSignature(
  tiles: ReadonlyArray<SceneNode>,
  codes?: ReadonlyMap<string, number>,
): string {
  let h = 2166136261;
  const mix = (v: number) => {
    h = Math.imul(h ^ (v | 0), 16777619);
  };
  for (const t of tiles) {
    const p = t.props as Record<string, unknown> | undefined;
    const q = Number(p?.q ?? 0);
    const r = Number(p?.r ?? 0);
    mix(q);
    mix(r);
    mix(Number(p?.riverEdges ?? 0));
    mix(Number(p?.level ?? 0));
    mix(codes?.get(`${q},${r}`) ?? 0);
  }
  mix(codes ? 1 : 0);
  return `${(h >>> 0).toString(16)}:${tiles.length}`;
}

let cached: TileData | null = null;

/** The board's per-tile flag texture (cached by signature; a different board replaces the single cached entry). */
export function tileDataFor(
  tiles: ReadonlyArray<SceneNode>,
  codes?: ReadonlyMap<string, number>,
): TileData {
  const signature = tileDataSignature(tiles, codes);
  if (cached && cached.signature === signature) return cached;

  const n = tiles.length;
  let width = 64;
  let height = 64;
  while (width * height < n) {
    if (width <= height) width *= 2;
    else height *= 2;
  }
  const data = new Uint8Array(width * height * 4);
  tiles.forEach((t, i) => {
    const p = t.props as Record<string, unknown> | undefined;
    const q = Number(p?.q ?? 0);
    const r = Number(p?.r ?? 0);
    data[i * 4] = Number(p?.riverEdges ?? 0) & 63;
    data[i * 4 + 1] = codes?.get(`${q},${r}`) ?? 0;
    data[i * 4 + 2] = Number(p?.level ?? 0);
    // A stays 0: the next flag's room.
  });

  const texture = new THREE.DataTexture(
    data,
    width,
    height,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
  );
  texture.name = 'tileData';
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  cached = { signature, texture, width, height };
  return cached;
}

/** Clear the cache (tests). */
export function clearTileDataCache(): void {
  cached = null;
}
